// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unused-vars, prefer-const, react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon, Select } from "semantic-ui-react";
import * as flightApi from "../../../../../apis/flightApi.js";
import * as orthoApi from "../../../../../apis/orthoApi.js";
import { useAuth0 } from "../../../../../auth/auth0/index.js";
import { useModuleNavigation } from "../../../../../hooks/useModuleNavigation.js";
import { useUserAuth } from "../../../../../hooks/useUserAuth.js";
import { useViewerDispatch } from "../StateManagement";
import { DateTimeSlider, OrthoOptions } from "./";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DateTime } from "luxon";

type Props = {
	minimized: boolean;
};

export const OrthoDropdown = ({ minimized }: Props) => {
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();
	const { updateViewerMapboxUploads, updateOrthoVisible, updateSelectedOrthoFlight } = useViewerDispatch();

	const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } = useSortable({
		id: "ortho",
	});

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	const [orthoId, setOrthoId] = useState<string>();
	const [orthoVisibleLocal, setOrthoVisibleLocal] = useState(true);
	const [selectedOrthoFlight, setSelectedOrthoFlight] = useState<string>();
	const [histogramBuckets, setHistogramBuckets] = useState([]);
	const [selectedTimeValue, setSelectedTimeValue] = useState(0);
	const hasInitiallyFit = useRef(false);

	useEffect(() => {
		updateSelectedOrthoFlight(selectedOrthoFlight);
	}, [selectedOrthoFlight]);

	const getOrthoOptions = async () => {
		const accessToken = await getTokenSilently();
		const json = flightApi.getViewerOrthoOptionsAsync(
			null,
			null,
			moduleNavigation.trialId,
			userAuth.currentClientId!,
			accessToken,
		);
		return json;
	};

	const getViewerImage = async () => {
		if (selectedOrthoFlight) {
			const accessToken = await getTokenSilently();
			const [flightId, flightImageId] = selectedOrthoFlight.split("/");
			const json = orthoApi.getViewerFlightImageAsync(flightId, flightImageId, userAuth.currentClientId, accessToken);
			return json;
		}
		return null;
	};

	const { isLoading: orthoOptionsLoading, data: orthoOptionData } = useQuery({
		queryKey: ["mfa viewer ortho options", userAuth.currentClientId, moduleNavigation.trialId],
		queryFn: () => getOrthoOptions(),
		select: (data) => data.data,
		enabled: !!moduleNavigation.trialId && !!userAuth.currentClientId,
	});

	const { isLoading: viewerImageLoading, data: viewerImageData } = useQuery({
		queryKey: ["mfa viewer image", userAuth.currentClientId, selectedOrthoFlight],
		queryFn: () => getViewerImage(),
		select: (data) => data.data,
		enabled: !!selectedOrthoFlight && !!userAuth.currentClientId,
	});

	const orthoOptions = useMemo(() => {
		if (orthoOptionData) {
			let orthoOpts = _.map(
				_.uniqBy(orthoOptionData.flightImages, "orthoImageTypeId") as any,
				({ orthoImageTypeId }) => {
					const orthoImageType = _.find(orthoOptionData.flightSensors, { orthoImageTypeId: orthoImageTypeId });
					const opt = {
						key: orthoImageTypeId,
						value: orthoImageTypeId,
						text: orthoImageType?.displayName ?? orthoImageType?.orthoImageType ?? "",
					};
					return opt;
				},
			);

			orthoOpts = _.sortBy(
				orthoOpts,
				[(opt) => _.find(orthoOptionData.flightSensors, { orthoImageTypeId: opt.key }).order || Infinity, "text"],
				["desc"],
			);
			return orthoOpts;
		}
		return [];
	}, [orthoOptionData]);

	const mappedDates = useMemo(() => {
		if (orthoOptionData && orthoId) {
			const mappedDates = _.reduce<
				any,
				{ [timeValue: number]: { flightId: string; flightImageId: string; text: string } }
			>(
				orthoOptionData.flightImages,
				(acc, fi, i) => {
					if (fi.orthoImageTypeId === orthoId) {
						const date = DateTime.fromISO(fi.flightDateAndTime);
						// HACK: add index i to avoid collisions (this may put things too close to eachother)
						acc[date.valueOf() + Number(i)] = {
							flightId: fi.flightId,
							flightImageId: fi.flightImageId,
							text: date.toLocal().toFormat("LL/dd/yy"),
						};
					}
					return acc;
				},
				{},
			);

			return mappedDates;
		}
		return {};
	}, [orthoId, orthoOptionData]);

	const dateOptions = useMemo(() => {
		if (_.keys(mappedDates).length !== 0) {
			let dateOpts = _.reduce(
				_.entries(mappedDates),
				(acc, [timeValue, fi]) => {
					acc.push({
						key: timeValue,
						value: timeValue,
						text: fi.text,
					});
					return acc;
				},
				[] as { key: string; value: string; text: string }[],
			);

			dateOpts = _.sortBy(dateOpts, [(opt) => Number(opt.key)], ["asc"]);
			return dateOpts;
		}
		return [];
	}, [mappedDates]);

	useEffect(() => {
		if (orthoOptionData) {
			const flights = orthoOptionData.flightImages;
			const sensors = orthoOptionData.flightSensors;

			let laiIndex = _.findIndex(flights, {
				orthoImageTypeId: _.find(sensors, { orthoImageType: "LAI" })?.orthoImageTypeId,
			});
			let laim3mIndex = _.findIndex(flights, {
				orthoImageTypeId: _.find(sensors, { orthoImageType: "LAI_M3M" })?.orthoImageTypeId,
			});
			let ndviIndex = _.findIndex(flights, {
				orthoImageTypeId: _.find(sensors, { orthoImageType: "NDVI" })?.orthoImageTypeId,
			});
			let ndvim3mIndex = _.findIndex(flights, {
				orthoImageTypeId: _.find(sensors, { orthoImageType: "NDVI_M3M" })?.orthoImageTypeId,
			});
			let sndviIndex = _.findIndex(flights, {
				orthoImageTypeId: _.find(sensors, { orthoImageType: "SNDVI" })?.orthoImageTypeId,
			});
			let rgbIndex = _.findIndex(flights, {
				orthoImageTypeId: _.find(sensors, { orthoImageType: "RGB" })?.orthoImageTypeId,
			});
			let rapidIndex = _.findIndex(flights, {
				orthoImageTypeId: _.find(sensors, { orthoImageType: "Rapid_RGB" })?.orthoImageTypeId,
			});

			let imageIndex =
				laiIndex !== -1
					? laiIndex
					: laim3mIndex !== -1
						? laim3mIndex
						: ndviIndex !== -1
							? ndviIndex
							: ndvim3mIndex !== -1
								? ndvim3mIndex
								: sndviIndex !== -1
									? sndviIndex
									: rgbIndex !== -1
										? rgbIndex
										: rapidIndex !== -1
											? rapidIndex
											: 0;

			setOrthoId(flights[imageIndex]?.orthoImageTypeId);
		}
	}, [orthoOptionData]);

	useEffect(() => {
		if (_.keys(mappedDates).length !== 0) {
			// const pairedFlights = _.entries(mappedDates);

			const orderedPairedFlights = _.chain(mappedDates)
				.entries()
				.orderBy(([timeValue, _]) => Number(timeValue) || Infinity, ["asc"])
				// .map(([_, flightInfo]) => flightInfo)
				.uniq()
				.value();

			const midDate = orderedPairedFlights?.[Math.floor((orderedPairedFlights.length - 1) / 2)];

			// const mid = _.find(flights, { flightDateAndTime: midDate });
			// const midId = midDate[1].flightId + "/" + midDate[1].flightImageId;

			setSelectedTimeValue(Number(midDate[0]));
			// setSelectedOrthoFlight(midId);
		}
	}, [mappedDates]);

	useEffect(() => {
		hasInitiallyFit.current = false;
	}, [orthoId]);

	useEffect(() => {
		if (viewerImageLoading) {
			updateViewerMapboxUploads([]);
			setHistogramBuckets([]);
		} else if (viewerImageData) {
			updateViewerMapboxUploads(viewerImageData.mapboxUploads);
			if (viewerImageData.imageBands) {
				let band = _.find(
					viewerImageData.imageBands,
					(e) => e.colorInterpretation === "Gray" || e.colorInterpretation === "Green",
				);
				if (band?.histogram) {
					setHistogramBuckets(band.histogram.buckets);
				} else {
					setHistogramBuckets([]);
				}
			}

			// if (!hasInitiallyFit.current) {
			// 	const fitToBounds = () => {
			// 		MFAViewer?.fitBounds(
			// 			[
			// 				[Number(viewerImageData.llLong), Number(viewerImageData.llLat)],
			// 				[Number(viewerImageData.urLong), Number(viewerImageData.urLat)],
			// 			],
			// 			{ bearing: 75 },
			// 		);
			// 		hasInitiallyFit.current = true;
			// 	};

			// 	MFAViewer?.on("load", fitToBounds);
			// 	// in case the mfa viewer loads in the middle of or before this call
			// 	fitToBounds();

			// 	return () => {
			// 		MFAViewer?.off("load", fitToBounds);
			// 	};
			// }
		}
		return () => {};
	}, [viewerImageLoading, viewerImageData]);

	useEffect(() => {
		if (selectedTimeValue && mappedDates[selectedTimeValue]) {
			const selectedDate = mappedDates[selectedTimeValue];
			setSelectedOrthoFlight(selectedDate.flightId + "/" + selectedDate.flightImageId);
		}
	}, [selectedTimeValue, mappedDates]);

	return (
		<div ref={setNodeRef} className="layerBlock" style={style} {...attributes}>
			<div ref={setActivatorNodeRef} className="layerBlockBar" {...listeners}>
				<Icon className="drag-handle-vertical" />
			</div>
			<Icon className="layerBlockIcon image" size="large" />
			<span className="layerBlockLabel">
				{minimized ? orthoOptions.find((o) => o.value === orthoId)?.text : "Imagery"}
			</span>
			<div className="layerBlockButtons">
				<Button
					className="actionsPopupTrigger iconButton"
					icon
					onClick={() => {
						updateOrthoVisible(!orthoVisibleLocal);
						setOrthoVisibleLocal((prev) => !prev);
					}}
				>
					<Icon className={`eye${orthoVisibleLocal ? "" : " closed"}`} size="large" />
				</Button>
				<OrthoOptions histogramBuckets={histogramBuckets} />
			</div>
			<Select
				className="layerBlockSelect"
				loading={orthoOptionsLoading}
				fluid
				options={orthoOptions}
				value={orthoId}
				onChange={(_, data) => {
					setOrthoId(data.value as string);
				}}
			/>
			<DateTimeSlider
				className="layerBlockDateSlider"
				mappedDates={mappedDates}
				selectedDate={selectedTimeValue}
				onChange={(date) => {
					if (mappedDates[date]) {
						setSelectedTimeValue(date);
					}
					// console.log(mappedDates[date]);
				}}
			/>
			<Select
				className="layerBlockDatePicker"
				loading={orthoOptionsLoading}
				compact
				icon
				options={dateOptions}
				value={selectedTimeValue.toString()}
				onChange={(_, data) => {
					setSelectedTimeValue(Number(data.value));
				}}
			/>
		</div>
	);
};
